import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Users, Plus, Edit, Trash2, Mail, Phone, Search, Download, Upload, Check, X, FileText, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { SuccessNotification, SUCCESS_CONFIGS } from '../components/SuccessNotification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Coproprietaires = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [copros, setCopros] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingCopro, setEditingCopro] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBuilding, setFilterBuilding] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState('nom');
  const [sortOrder, setSortOrder] = useState('asc');
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', telephone: '', building_id: '', lot: '', tantiemes: 100
  });

  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successConfig, setSuccessConfig] = useState(null);
  const [notificationType, setNotificationType] = useState('COPRO_ADDED');

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    try {
      const [coprosRes, buildingsRes] = await Promise.all([
        axios.get(`${API}/coproprietaires`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/immeubles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCopros(coprosRes.data);
      setBuildings(buildingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCreating = !editingCopro;
    try {
      if (editingCopro) {
        await axios.put(`${API}/coproprietaires/${editingCopro.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/coproprietaires`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingCopro(null);
      setFormData({ nom: '', prenom: '', email: '', telephone: '', building_id: '', lot: '', tantiemes: 100 });
      fetchData();
      
      // Show success notification only for new coproprietaire
      if (isCreating) {
        setNotificationType('COPRO_ADDED');
        setSuccessConfig(SUCCESS_CONFIGS.COPRO_ADDED);
        setShowSuccessNotification(true);
      }
    } catch (error) {
      console.error('Error saving copro:', error);
    }
  };

  const handleEdit = (copro) => {
    setEditingCopro(copro);
    setFormData({
      nom: copro.nom || '', prenom: copro.prenom || '', email: copro.email || '',
      telephone: copro.telephone || '', building_id: copro.building_id || '',
      lot: copro.lot || '', tantiemes: copro.tantiemes || 100
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce coproprietaire ?')) {
      try {
        await axios.delete(`${API}/coproprietaires/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchData();
      } catch (error) {
        console.error('Error deleting copro:', error);
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getBuildingDisplayName = (building) => {
    if (!building) return '';
    const nom = building.nom || '';
    const adresse = building.adresse || '';
    const codePostal = building.code_postal || '';
    const ville = building.ville || '';
    
    let display = nom;
    if (adresse) {
      display += display ? ` - ${adresse}` : adresse;
    }
    if (codePostal || ville) {
      display += `, ${codePostal} ${ville}`.trim();
    }
    return display || 'Immeuble inconnu';
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCopros.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCopros.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/coproprietaires/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const csvContent = response.data.csv_content;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename || 'coproprietaires.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Erreur lors de l\'export');
    }
  };

  // Import from CSV
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportResult(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvContent = event.target.result;
        const response = await axios.post(`${API}/coproprietaires/import`, 
          { csv_content: csvContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setImportResult({
          success: true,
          message: response.data.message,
          imported: response.data.imported,
          errors: response.data.errors || []
        });
        
        fetchData();
        
        // Show success notification for successful import with at least 1 record
        if (response.data.imported > 0) {
          setShowImportModal(false);
          setNotificationType('IMPORT_COPRO_SUCCESS');
          setSuccessConfig({
            ...SUCCESS_CONFIGS.IMPORT_COPRO_SUCCESS,
            message: `${response.data.imported} coproprietaire(s) importé(s) avec succès depuis votre fichier CSV.`
          });
          setShowSuccessNotification(true);
        }
      } catch (error) {
        setImportResult({
          success: false,
          message: error.response?.data?.detail || 'Erreur lors de l\'import',
          errors: []
        });
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = 'NUM IMMEUBLE;NOM IMMEUBLE;NUM COPROPRIETAIRE;civilite;nom coproprietaire;prenom;adresse;CP;ville;mail;tel;num lot;tantieme;base  de repartation;LIBELLE BASE DE REPART\n170;CLASSIC RED;1;Madame et Monsieur;DUPONT;JEAN/MARIE;12 rue exemple;75001;PARIS;jean.dupont@email.com;06 12 34 56 78;1;706;CCG;CHARGES COMMUNES GENERALES\n';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_coproprietaires.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredCopros = copros
    .filter(c => {
      const matchSearch = `${c.nom} ${c.prenom} ${c.email}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchBuilding = !filterBuilding || c.building_id === filterBuilding;
      return matchSearch && matchBuilding;
    })
    .sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      if (sortOrder === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      }
      return bVal.toString().localeCompare(aVal.toString());
    });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="coproprietaires-page">
      {/* Header - Style unifié */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <Users className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mes Copropriétaires</h1>
            <p className="text-gray-500">Gérez les coproprietaires de vos immeubles</p>
          </div>
        </div>
        <button 
          onClick={() => { setEditingCopro(null); setFormData({ nom: '', prenom: '', email: '', telephone: '', building_id: '', lot: '', tantiemes: 100 }); setShowModal(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-full font-medium shadow-md hover:bg-[#00B892] transition-all"
          data-testid="new-copro-btn"
        >
          <Plus size={18} /><span>Nouveau coproprietaire</span>
        </button>
      </div>

      {/* Actions bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <button 
            onClick={toggleSelectAll}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Check size={16} className={selectedIds.length === filteredCopros.length && filteredCopros.length > 0 ? 'text-[#00D3A8]' : 'text-gray-400'} />
            <span>Tout sélectionner</span>
          </button>
          <button 
            onClick={handleImportClick}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
            data-testid="import-copro-btn"
          >
            <Upload size={16} />
            <span>Importer</span>
          </button>
          <button 
            onClick={handleExport}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
            data-testid="export-copro-btn"
          >
            <Download size={16} />
            <span>Exporter</span>
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Voir</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="border border-slate-200 rounded-lg px-2 py-1 text-slate-700 text-xs md:text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all text-sm" 
          />
        </div>
        <select 
          value={filterBuilding} 
          onChange={(e) => setFilterBuilding(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all text-sm"
        >
          <option value="">Tous les immeubles</option>
          {buildings.map(b => <option key={b.id} value={b.id}>{getBuildingDisplayName(b)}</option>)}
        </select>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 overflow-x-auto">
        <table className="w-full" data-testid="copros-table">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="w-12 px-4 py-3">
                <input 
                  type="checkbox" 
                  checked={selectedIds.length === filteredCopros.length && filteredCopros.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 text-[#00D3A8] focus:ring-[#00D3A8]"
                />
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                onClick={() => handleSort('nom')}
              >
                <div className="flex items-center space-x-1">
                  <span>Nom</span>
                  {sortField === 'nom' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Immeuble</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Lot</th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                onClick={() => handleSort('tantiemes')}
              >
                <div className="flex items-center space-x-1">
                  <span>Tantièmes</span>
                  {sortField === 'tantiemes' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th className="w-24 px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredCopros.slice(0, itemsPerPage).map((copro) => (
              <tr key={copro.id} className="hover:bg-slate-50 transition-colors" data-testid={`copro-row-${copro.id}`}>
                <td className="px-4 py-3">
                  <input 
                    type="checkbox"
                    checked={selectedIds.includes(copro.id)}
                    onChange={() => toggleSelect(copro.id)}
                    className="w-4 h-4 rounded border-slate-300 text-[#00D3A8] focus:ring-[#00D3A8]"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 bg-[#00D3A8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#00D3A8] font-medium text-sm">{copro.prenom?.[0]}{copro.nom?.[0]}</span>
                    </div>
                    <span className="font-medium text-slate-700">{copro.prenom} {copro.nom}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center text-slate-500"><Mail size={14} className="mr-1.5" />{copro.email}</div>
                  {copro.telephone && <div className="flex items-center text-slate-400 mt-1"><Phone size={14} className="mr-1.5" />{copro.telephone}</div>}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500">
                  {getBuildingDisplayName(buildings.find(b => b.id === copro.building_id))}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500">{copro.lot || '-'}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700">{copro.tantiemes}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-2">
                    <button 
                      onClick={() => handleEdit(copro)} 
                      className="p-1.5 text-slate-400 hover:text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(copro.id)} 
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredCopros.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Users size={40} className="mx-auto mb-3 text-slate-200" strokeWidth={1.5} />
            <p className="text-sm">Aucun coproprietaire trouvé</p>
          </div>
        )}

        {/* Pagination */}
        {filteredCopros.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
            <span>{filteredCopros.length} coproprietaire(s)</span>
            <div className="flex items-center space-x-2">
              <button className="px-2 py-1 hover:bg-slate-100 rounded transition-colors">←</button>
              <span className="px-3 py-1 bg-[#00D3A8] text-white rounded">1</span>
              <button className="px-2 py-1 hover:bg-slate-100 rounded transition-colors">→</button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {filteredCopros.slice(0, itemsPerPage).map((copro) => (
          <div 
            key={copro.id}
            className="bg-white rounded-xl shadow-sm border border-slate-100 p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  checked={selectedIds.includes(copro.id)}
                  onChange={() => toggleSelect(copro.id)}
                  className="w-4 h-4 rounded border-slate-300 text-[#00D3A8] focus:ring-[#00D3A8]"
                />
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00D3A8] to-[#00C7A1] flex items-center justify-center text-white font-semibold text-sm">
                  {copro.prenom?.[0]}{copro.nom?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{copro.prenom} {copro.nom}</p>
                  {copro.building_id && (
                    <p className="text-xs text-[#00D3A8]">
                      {buildings.find(b => b.id === copro.building_id)?.nom || ''}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-1">
                <button 
                  onClick={() => handleEdit(copro)} 
                  className="p-1.5 text-slate-400 hover:text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button 
                  onClick={() => handleDelete(copro.id)} 
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <div className="space-y-1.5 text-sm pl-7">
              {copro.email && (
                <div className="flex items-center text-slate-600">
                  <Mail size={14} className="mr-2 text-slate-400" />
                  <span className="truncate">{copro.email}</span>
                </div>
              )}
              {copro.telephone && (
                <div className="flex items-center text-slate-600">
                  <Phone size={14} className="mr-2 text-slate-400" />
                  <span>{copro.telephone}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-slate-500 pt-2 border-t border-slate-100 mt-2">
                <span>Lot: {copro.lot || '-'}</span>
                <span className="font-medium text-[#00D3A8]">{copro.tantiemes || 0} tantièmes</span>
              </div>
            </div>
          </div>
        ))}
        
        {filteredCopros.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
            <Users size={40} className="mx-auto mb-3 text-slate-200" strokeWidth={1.5} />
            <p className="text-sm text-slate-400">Aucun coproprietaire trouvé</p>
          </div>
        )}
        
        {/* Mobile pagination info */}
        {filteredCopros.length > 0 && (
          <div className="text-center text-sm text-slate-500 py-2">
            {filteredCopros.length} coproprietaire(s)
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Importer des coproprietaires
              </h2>
              <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-medium text-slate-700 mb-2 flex items-center">
                  <FileText size={18} className="mr-2 text-[#00D3A8]" />
                  Format du fichier CSV
                </h3>
                <p className="text-sm text-slate-500 mb-3">
                  Séparateur : <strong>point-virgule (;)</strong>. Colonnes requises :
                </p>
                <code className="text-xs bg-white px-3 py-2 rounded border border-slate-200 block overflow-x-auto">
                  NUM IMMEUBLE; NOM IMMEUBLE; civilite; nom coproprietaire; prenom; adresse; CP; ville; mail; tel; num lot; tantieme; base de repartation
                </code>
                <p className="text-xs text-slate-400 mt-2">
                  Note: Les tantièmes CCG seront additionnés. Les immeubles seront créés automatiquement s'ils n'existent pas.
                </p>
                <button 
                  onClick={downloadTemplate}
                  className="mt-3 text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Télécharger le modèle CSV
                </button>
              </div>

              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-[#00D3A8] transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload-copro"
                />
                <label htmlFor="csv-upload-copro" className="cursor-pointer">
                  <Upload size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600 font-medium">Cliquez pour sélectionner un fichier CSV</p>
                  <p className="text-sm text-slate-400 mt-1">ou glissez-déposez le fichier ici</p>
                </label>
              </div>

              {importing && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00D3A8] border-t-transparent mr-3"></div>
                  <span className="text-slate-600">Import en cours...</span>
                </div>
              )}

              {importResult && (
                <div className={`rounded-lg p-4 ${importResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-start">
                    {importResult.success ? (
                      <Check size={20} className="text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={20} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${importResult.success ? 'text-emerald-700' : 'text-red-700'}`}>
                        {importResult.message}
                      </p>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <ul className="mt-2 text-sm text-red-600 space-y-1">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li className="text-red-500">... et {importResult.errors.length - 5} autres erreurs</li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button 
                onClick={() => setShowImportModal(false)}
                className="px-5 py-2.5 border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 font-medium transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-slate-800 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingCopro ? 'Modifier' : 'Nouveau'} coproprietaire
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Prénom *</label>
                  <input 
                    type="text" 
                    value={formData.prenom} 
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Nom *</label>
                  <input 
                    type="text" 
                    value={formData.nom} 
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                    required 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                <input 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Téléphone</label>
                <input 
                  type="tel" 
                  value={formData.telephone} 
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Immeuble</label>
                <select 
                  value={formData.building_id} 
                  onChange={(e) => setFormData({...formData, building_id: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                >
                  <option value="">Sélectionner un immeuble...</option>
                  {buildings.map(b => <option key={b.id} value={b.id}>{getBuildingDisplayName(b)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">N° Lot</label>
                  <input 
                    type="text" 
                    value={formData.lot} 
                    onChange={(e) => setFormData({...formData, lot: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Tantièmes</label>
                  <input 
                    type="number" 
                    value={formData.tantiemes} 
                    onChange={(e) => setFormData({...formData, tantiemes: parseInt(e.target.value) || 100})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-5 py-2.5 border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 font-medium transition-colors"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="px-5 py-2.5 text-white rounded-full font-medium shadow-md hover:shadow-lg transition-all"
                  style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {successConfig && (
        <SuccessNotification
          isOpen={showSuccessNotification}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessConfig(null);
          }}
          title={successConfig.title}
          message={successConfig.message}
          nextStep={successConfig.nextStep}
          notificationType={notificationType}
          autoCloseDelay={8000}
        />
      )}
    </div>
  );
};

export default Coproprietaires;
